/**
 * Pricing matrix — accounts (rows) × billing channels (columns). Each cell is
 * an editable credits-per-message value: it shows the account-level override in
 * bold, or the platform default (dimmed) when there is no override. Editing a
 * cell saves an override on blur/Enter; clearing it reverts to the default.
 *
 * Each row also has a tariff selector. Pick a preset tariff and the row's prices
 * come from it and the cells lock (read-only); leave it on "Default" to price
 * per cell. An account filter narrows the visible rows.
 *
 * Billing is per CommunicationType (8 channels); the 17 display channels
 * collapse here (Slack/Teams/... → TeamChat, web/app push → Push).
 */

import React from "react";
import { useNotify, useTranslate } from "react-admin";
import {
  Box,
  CircularProgress,
  InputBase,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import { useCanEdit } from "@/utils/permissions";
import {
  assignRowPlan,
  getPricingMatrix,
  setPricingCell,
  type PricingMatrix as Matrix,
} from "../services/pricingMatrixService";

const i18nBase = "resources.credit-consumption/pricing";

interface CellProps {
  accountId: string;
  channel: string;
  applicationId: string | null;
  override: number | undefined;
  defaultValue: number;
  canEdit: boolean;
  locked: boolean;
  lockedValue: number | undefined;
  onSaved: (accountId: string, channel: string, value: number | null) => void;
}

const MatrixCell: React.FC<CellProps> = ({
  accountId,
  channel,
  applicationId,
  override,
  defaultValue,
  canEdit,
  locked,
  lockedValue,
  onSaved,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const theme = useTheme();
  const isOverride = override !== undefined;
  const [value, setValue] = React.useState<string>(
    isOverride ? String(override) : "",
  );
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    setValue(isOverride ? String(override) : "");
  }, [override, isOverride]);

  const commit = async (): Promise<void> => {
    const trimmed = value.trim();
    const parsed = trimmed === "" ? null : Number(trimmed);
    if (parsed !== null && (Number.isNaN(parsed) || parsed < 0)) {
      setValue(isOverride ? String(override) : "");
      return;
    }
    // No change.
    const current = isOverride ? override : null;
    if (parsed === current) return;

    setBusy(true);
    try {
      await setPricingCell(accountId, channel, parsed, applicationId);
      onSaved(accountId, channel, parsed);
    } catch (err: unknown) {
      notify(extractErrorMessage(err, "ra.notification.http_error", translate), {
        type: "error",
      });
      setValue(isOverride ? String(override) : "");
    } finally {
      setBusy(false);
    }
  };

  // Locked by a tariff: show the tariff price (or the default when the tariff
  // doesn't set this channel), read-only.
  if (locked) {
    const usesDefault = lockedValue === undefined;
    const shown = usesDefault ? defaultValue : lockedValue;
    return (
      <Box
        sx={{
          px: 1,
          py: 0.75,
          textAlign: "right",
          borderLeft: `1px solid ${theme.palette.divider}`,
          minWidth: 78,
          fontSize: 13,
          fontVariantNumeric: "tabular-nums",
          color: usesDefault ? "text.disabled" : "text.primary",
          fontWeight: usesDefault ? 400 : 600,
          bgcolor: "action.disabledBackground",
        }}
      >
        {shown}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        px: 1,
        py: 0.5,
        borderLeft: `1px solid ${theme.palette.divider}`,
        minWidth: 78,
      }}
    >
      <InputBase
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
        disabled={!canEdit || busy}
        type="number"
        placeholder={String(defaultValue)}
        inputProps={{
          min: 0,
          step: "any",
          style: { textAlign: "right", padding: "4px 6px", fontVariantNumeric: "tabular-nums" },
        }}
        sx={{
          width: "100%",
          fontSize: 13,
          fontWeight: isOverride ? 700 : 400,
          color: isOverride ? "text.primary" : "text.disabled",
          borderRadius: 1,
          bgcolor: isOverride ? "action.hover" : "transparent",
          "& input::placeholder": { opacity: 0.6 },
        }}
      />
      {busy && (
        <CircularProgress
          size={12}
          sx={{ position: "absolute", right: 2, top: 8 }}
        />
      )}
    </Box>
  );
};

export const PricingMatrix: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const theme = useTheme();
  const canEdit = useCanEdit();
  const [matrix, setMatrix] = React.useState<Matrix | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("");
  const [appId, setAppId] = React.useState<string>(""); // "" = account level (all apps)
  const [busyPlanRows, setBusyPlanRows] = React.useState<Set<string>>(new Set());

  const load = React.useCallback(
    (application: string): void => {
      setLoading(true);
      getPricingMatrix(application || null)
        .then(setMatrix)
        .catch((err: unknown) => {
          notify(extractErrorMessage(err, "ra.notification.http_error", translate), {
            type: "error",
          });
        })
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  React.useEffect(() => {
    load(appId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  const onSaved = (accountId: string, channel: string, value: number | null): void => {
    setMatrix((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        rows: prev.rows.map((r) => {
          if (r.accountId !== accountId) return r;
          const overrides = { ...r.overrides };
          const key = channel.toLowerCase();
          if (value === null) delete overrides[key];
          else overrides[key] = value;
          return { ...r, overrides };
        }),
      };
    });
  };

  const onPlanChange = async (accountId: string, planId: string): Promise<void> => {
    setBusyPlanRows((s) => new Set(s).add(accountId));
    try {
      await assignRowPlan(accountId, planId === "" ? null : planId, appId || null);
      setMatrix((prev) =>
        prev
          ? {
              ...prev,
              rows: prev.rows.map((r) =>
                r.accountId === accountId
                  ? { ...r, pricingPlanId: planId === "" ? null : planId }
                  : r,
              ),
            }
          : prev,
      );
    } catch (err: unknown) {
      notify(extractErrorMessage(err, "ra.notification.http_error", translate), {
        type: "error",
      });
    } finally {
      setBusyPlanRows((s) => {
        const next = new Set(s);
        next.delete(accountId);
        return next;
      });
    }
  };

  const headerCell = {
    px: 1,
    py: 1,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap" as const,
    borderLeft: `1px solid ${theme.palette.divider}`,
  };

  const plansById = React.useMemo(() => {
    const map = new Map<string, Matrix["plans"][number]>();
    matrix?.plans.forEach((p) => map.set(p.id, p));
    return map;
  }, [matrix]);

  const visibleRows = React.useMemo(() => {
    if (!matrix) return [];
    const f = filter.trim().toLowerCase();
    if (!f) return matrix.rows;
    return matrix.rows.filter(
      (r) =>
        r.accountName.toLowerCase().includes(f) ||
        r.costCenterName.toLowerCase().includes(f),
    );
  }, [matrix, filter]);

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, ml: 2 }}>
        {translate(`${i18nBase}.name`, { smart_count: 2 })}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: "text.secondary", ml: 2, mb: 2, maxWidth: 900 }}>
        {translate(`${i18nBase}.matrix.hint`)}
      </Typography>

      {matrix && (
        <Box sx={{ ml: 2, mb: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={translate(`${i18nBase}.matrix.filterAccount`)}
            sx={{ minWidth: 260 }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              {translate(`${i18nBase}.matrix.application`)}:
            </Typography>
            <Select
              size="small"
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              displayEmpty
              sx={{ fontSize: 13, minWidth: 220 }}
            >
              <MenuItem value="">
                <em>{translate(`${i18nBase}.matrix.accountLevel`)}</em>
              </MenuItem>
              {matrix.applications.map((a) => (
                <MenuItem key={a.id} value={a.id} sx={{ fontSize: 13 }}>
                  {a.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
          <CircularProgress size={26} />
        </Box>
      ) : !matrix || matrix.rows.length === 0 ? (
        <Typography sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
          {translate(`${i18nBase}.matrix.empty`)}
        </Typography>
      ) : (
        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            overflow: "auto",
            maxWidth: "100%",
          }}
        >
          <Box
            component="table"
            sx={{ borderCollapse: "collapse", width: "100%", minWidth: 720 }}
          >
            <Box component="thead">
              <Box component="tr" sx={{ bgcolor: "action.hover" }}>
                <Box
                  component="th"
                  sx={{
                    ...headerCell,
                    borderLeft: "none",
                    textAlign: "left",
                    position: "sticky",
                    left: 0,
                    bgcolor: theme.palette.background.paper,
                    minWidth: 200,
                  }}
                >
                  {translate(`${i18nBase}.matrix.account`)}
                </Box>
                <Box component="th" sx={{ ...headerCell, textAlign: "left", minWidth: 170 }}>
                  {translate(`${i18nBase}.matrix.tariff`)}
                </Box>
                {matrix.channels.map((ch) => (
                  <Box component="th" key={ch} sx={{ ...headerCell, textAlign: "right" }}>
                    <Tooltip title={`${translate(`${i18nBase}.matrix.default`)}: ${matrix.defaults[ch.toLowerCase()] ?? "—"}`}>
                      <span>{ch}</span>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {visibleRows.map((row) => {
                const appScoped = appId !== "";
                // Effective tariff: per-scope selection, or the inherited account-level
                // one when app-scoped and no per-app tariff is set.
                const effectivePlanId =
                  row.pricingPlanId ?? (appScoped ? row.inheritedPlanId : null);
                const plan = effectivePlanId
                  ? plansById.get(effectivePlanId)
                  : undefined;
                const locked = !!effectivePlanId;
                const inheritedPlan =
                  appScoped && row.inheritedPlanId
                    ? plansById.get(row.inheritedPlanId)
                    : undefined;
                return (
                  <Box
                    component="tr"
                    key={row.accountId}
                    sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
                  >
                    <Box
                      component="td"
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        position: "sticky",
                        left: 0,
                        bgcolor: theme.palette.background.paper,
                        borderRight: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>
                        {row.accountName}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "text.disabled", whiteSpace: "nowrap" }}>
                        {row.costCenterName}
                      </Typography>
                    </Box>
                    <Box
                      component="td"
                      sx={{ px: 1, py: 0.5, borderLeft: `1px solid ${theme.palette.divider}` }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Select
                          size="small"
                          value={row.pricingPlanId ?? ""}
                          onChange={(e) => void onPlanChange(row.accountId, e.target.value)}
                          disabled={!canEdit || busyPlanRows.has(row.accountId)}
                          displayEmpty
                          sx={{ fontSize: 13, minWidth: 150 }}
                        >
                          <MenuItem value="">
                            <em>
                              {appScoped
                                ? inheritedPlan
                                  ? translate(`${i18nBase}.matrix.inheritNamed`, {
                                      name: inheritedPlan.name,
                                    })
                                  : translate(`${i18nBase}.matrix.inherit`)
                                : translate(`${i18nBase}.matrix.noTariff`)}
                            </em>
                          </MenuItem>
                          {matrix.plans.map((p) => (
                            <MenuItem key={p.id} value={p.id} sx={{ fontSize: 13 }}>
                              {p.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {busyPlanRows.has(row.accountId) && <CircularProgress size={14} />}
                      </Box>
                    </Box>
                    {matrix.channels.map((ch) => (
                      <Box component="td" key={ch} sx={{ p: 0 }}>
                        <MatrixCell
                          accountId={row.accountId}
                          channel={ch}
                          applicationId={appId || null}
                          override={row.overrides[ch.toLowerCase()]}
                          defaultValue={matrix.defaults[ch.toLowerCase()] ?? 0}
                          canEdit={canEdit}
                          locked={locked}
                          lockedValue={plan?.prices[ch.toLowerCase()]}
                          onSaved={onSaved}
                        />
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PricingMatrix;
